import { type ConnectorGroupItem } from "~/ui-rtk/pages/Onboarding/ConnectSources/types"
import SourceItem from "../SourceItem/SourceItem"
import MetaAdvancedItem from "../MetaAdvancedItem"
import { META_ADVANCED_ANCHOR_SERVICE } from "../MetaAdvancedItem/visibility"
import ConnectionStatusDialog from "~/ui-rtk/components/features/connectors/ConnectionStatusDialog"
import SearchBar from "../SearchBar"
import {
  serviceConnectorMap,
  infoStepMap,
  preConnectFormMap,
} from "~/ui-rtk/components/features/connectors/forms/utils"

type ServiceConnectorConfig = {
  type: "connector" | "upload"
  component: React.ComponentType<Record<string, unknown>>
  showForStatuses?: string[]
}

import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import useAccess from "~/ui-rtk/hooks/access"
import useConnectedServices from "~/ui-rtk/hooks/connected-services"
import useConnectors from "~/ui-rtk/hooks/connectors"
import {
  isAirbyteConnector,
  isIngestionConnector,
} from "~/ui-rtk/utils/connector-utils"
import AdditionalConfigForm from "~/ui-rtk/components/features/connectors/forms/AdditionalConfigForm"
import IngestionForm from "~/ui-rtk/components/features/connectors/forms/ingestion"
import { useConnectorsTracking } from "~/ui-rtk/hooks/useConnectorsTracking"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"

export default function SourceItems({
  connectors,
  activeCategory,
  onSearch,
  integrationsReady = true,
  onRefetch,
  isRefetching = false,
  showMetaAdvanced = false,
}: {
  connectors: ConnectorGroupItem[]
  activeCategory: string
  onSearch?: (searchTerm: string) => void
  integrationsReady?: boolean
  onRefetch?: () => void
  isRefetching?: boolean
  /** The category gets the Meta Advanced row (APP-548); it shows to super admins only. */
  showMetaAdvanced?: boolean
}) {
  const [Dialog, setDialog] = useState<ReactNode | null>(null)
  const [onboardUrl, setOnboardUrl] = useState<string | null>(null)
  const isAddingConnection = useRef(false)
  const infoStepShownFor = useRef<string | null>(null)
  const preConnectValues = useRef<{
    service: string
    values: Record<string, unknown>
  } | null>(null)
  const [, setSearchParams] = useSearchParams()
  const { connectedServices, getCompanyConnectorByService, refetchConnectors } =
    useConnectedServices()
  const { connect, reAuth, getConnectorById } = useConnectors()
  const { isSuperAdmin } = useAccess()
  const {
    trackOAuthRedirect,
    trackConnectionSuccess,
    trackConnectionFailed,
    trackReconnectFailed,
    trackAddConnectionClicked,
  } = useConnectorsTracking()

  const closeDialog = () => {
    setDialog(null)
  }

  const connectToService = useCallback(
    async (
      service: string,
      callback?: CallableFunction,
      targetCompanyConnectorId?: string,
    ) => {
      if (!integrationsReady) {
        toast.info(
          "Preparing the connectors, just wait a couple of seconds before they are ready...",
          {
            toastId: "integrations-not-ready",
          },
        )
        void callback?.()
        return
      }

      // Resolve the existing connector. If the caller supplied a specific CC id
      // (per-CC reconnect from ConnectionList), target that one; otherwise fall
      // back to the single-CC lookup by service.
      const existingConnector = targetCompanyConnectorId
        ? connectedServices?.find(s => s.id === targetCompanyConnectorId)
        : getCompanyConnectorByService(service)
      const reAuthCompanyConnectorId = existingConnector?.id
      const isReconnect =
        !!existingConnector && existingConnector.status !== "NOT_CONNECTED"
      const trackFailure = isReconnect
        ? trackReconnectFailed
        : trackConnectionFailed

      // Check if connector was created by migration and block re-authentication
      if (existingConnector?.metadata?.createdByMigration) {
        toast.error(
          "This connector was created by the system migration and cannot be re-authenticated. Please contact your administrator if you need to modify this connection.",
          {
            toastId: "migration-connector-reauth-blocked",
          },
        )
        void callback?.()
        return
      }

      // Dialog-display guards live ABOVE the try so their early returns skip the
      // `finally { void callback?.() }` below. Inside the try, `finally` fired the
      // moment the dialog was DISPLAYED, releasing SourceItem's connecting guard
      // while the dialog was still open (second click -> parallel flow) and then
      // firing callback a second time on close. Out here, callback fires exactly
      // once per flow: in onClose, or via the re-invocation's own finally after the
      // real connect attempt resolves.

      // Check if this service has an info step to show before connecting
      const InfoStepComponent = infoStepMap[service]
      if (InfoStepComponent && infoStepShownFor.current !== service) {
        setDialog(() => (
          <InfoStepComponent
            isOpen
            onClose={() => {
              closeDialog()
              infoStepShownFor.current = null
              void callback?.()
            }}
            onContinue={() => {
              closeDialog()
              infoStepShownFor.current = service
              void connectToService(service, callback)
            }}
          />
        ))
        return
      }
      infoStepShownFor.current = null

      // Some Fivetran services (amazon_selling_partner) are rejected at
      // `POST /connectors` unless their config already carries a value, so collect
      // it here — before connect() — and send it as preConfig (MD-5391).
      const PreConnectForm = preConnectFormMap[service]
      if (PreConnectForm && preConnectValues.current?.service !== service) {
        setDialog(() => (
          <PreConnectForm
            isOpen
            onClose={() => {
              closeDialog()
              preConnectValues.current = null
              void callback?.()
            }}
            onSubmit={(preConfigValues: Record<string, unknown>) => {
              closeDialog()
              preConnectValues.current = {
                service,
                values: preConfigValues,
              }
              void connectToService(service, callback, targetCompanyConnectorId)
            }}
          />
        ))
        return
      }

      try {
        // Check if this is an Airbyte connector
        const connector = getConnectorById(service)

        // Check if this connector supports ingestion BEFORE connecting
        // This allows us to skip Fivetran/Airbyte flows for ingestion connectors
        if (connector && isIngestionConnector(connector)) {
          try {
            const { companyConnectorId } = await connect(service)
            if (!companyConnectorId) {
              trackFailure({
                service_name: service,
                connector_type: "ingestion",
                error_message: "Failed to create connector",
              })
              toast.error("Failed to create connector", {
                toastId: `create-ingestion-connector-error-${service}`,
              })
              return
            }
            trackConnectionSuccess({
              service_name: service,
              connector_type: "ingestion",
              company_connector_id: companyConnectorId,
            })
            setDialog(() => (
              <IngestionForm
                isOpen
                service={service}
                companyConnectorId={companyConnectorId}
                onClose={closeDialog}
              />
            ))
            return
          } catch (error) {
            trackFailure({
              service_name: service,
              connector_type: "ingestion",
              error_message: "Failed to create ingestion connector",
            })
            toast.error("Failed to create ingestion connector", {
              toastId: `create-ingestion-connector-error-${service}`,
            })
            return
          }
        }

        // Check if this is an Airbyte connector
        if (connector && isAirbyteConnector(connector)) {
          // For Airbyte connectors, always try to connect first
          // Backend will return requiresPreConfig if pre-config is required.
          // When we're targeting an existing CompanyConnector (per-CC reconnect
          // or single-CC reconnect auto-detected via service lookup), route
          // through /re-auth so the backend acts on the specific CC instead
          // of doing a service-name find-first.
          const initiateAirbyteFlow = (
            preConfigValues?: Record<string, unknown>,
          ) =>
            reAuthCompanyConnectorId
              ? reAuth(reAuthCompanyConnectorId, preConfigValues)
              : connect(service, preConfigValues)

          try {
            const {
              uri,
              requiresPreConfig = false,
              companyConnectorId,
            } = await initiateAirbyteFlow()

            if (requiresPreConfig && !uri) {
              if (companyConnectorId) {
                setDialog(() => (
                  <AdditionalConfigForm
                    companyConnectorId={companyConnectorId}
                    connector={connector}
                    isOpen
                    configPhase="oauth"
                    onSubmit={async (
                      preConfigValues: Record<string, unknown>,
                    ) => {
                      try {
                        const { uri } =
                          await initiateAirbyteFlow(preConfigValues)
                        if (uri) {
                          window.location.href = uri
                        }
                      } catch (error) {
                        toast.error(
                          "Failed to initiate OAuth with pre-config",
                          {
                            toastId: `oauth-preconfig-error-${service}`,
                          },
                        )
                        throw error
                      }
                    }}
                    onClose={closeDialog}
                  />
                ))
                return
              }

              toast.error("Failed to retrieve connector ID", {
                toastId: `connector-id-error-${service}`,
              })
              return
            }

            if (uri) {
              // API-key (and other non-OAuth) Airbyte connectors come back with a
              // `?autoOpen=<service>&companyConnectorId=<id>` URI pointing at the
              // current page. Setting those params in-place triggers the
              // ConnectorsTable autoOpen effect, which opens AdditionalConfigForm
              // without a full page reload. OAuth URIs are external and need
              // window.location navigation.
              const parsedUri = new URL(uri, window.location.origin)
              const autoOpenParam = parsedUri.searchParams.get("autoOpen")
              if (autoOpenParam) {
                // The new CompanyConnector was just created on the backend, but the
                // `connectedServices` cache is invalidated asynchronously. Without
                // waiting for it here, the URL params flip before the cache updates,
                // so the ConnectorsTable autoOpen effect finds no matching connector
                // and silently skips opening the modal on the first attempt (only a
                // page refresh re-fetches the cache and opens it).
                await refetchConnectors()
                setSearchParams(
                  prev => {
                    const merged = new URLSearchParams(prev)
                    parsedUri.searchParams.forEach((value, key) => {
                      merged.set(key, value)
                    })
                    return merged
                  },
                  { replace: true },
                )
                return
              }

              trackOAuthRedirect({
                service_name: service,
                connector_type: "airbyte",
                requires_pre_config: requiresPreConfig,
              })
              window.location.href = uri
              return
            }
          } catch (error) {
            trackFailure({
              service_name: service,
              connector_type: "airbyte",
              error_message: "Failed to create connector",
            })
            toast.error("Failed to create connector", {
              toastId: `create-connector-error-${service}`,
            })
            return
          }
        }

        // Existing Fivetran flow. The Merchant Token dialog for services in
        // preConnectFormMap already ran above the try; consume its values here.
        const preConfig =
          preConnectValues.current?.service === service
            ? preConnectValues.current.values
            : undefined
        preConnectValues.current = null

        const { companyConnectorId, uri } = await connect(service, preConfig)

        const connectorService: ServiceConnectorConfig | undefined =
          serviceConnectorMap[service] as ServiceConnectorConfig | undefined
        if (connectorService) {
          // Check if we should show the form for the current connector status
          const existingConnector = getCompanyConnectorByService(service)
          const currentStatus = existingConnector?.status

          // If showForStatuses is defined, only show form for those statuses
          if (connectorService.showForStatuses) {
            if (
              !currentStatus ||
              !connectorService.showForStatuses.includes(currentStatus)
            ) {
              // Don't show form, use OAuth flow instead
              setOnboardUrl(uri)
              return
            }
          }

          const { component: ConnectorDialog } = connectorService
          setDialog(() => (
            <ConnectorDialog
              isOpen
              uri={uri}
              service={service}
              companyConnectorId={companyConnectorId}
              onClose={closeDialog}
            />
          ))
          return
        }

        trackOAuthRedirect({
          service_name: service,
          connector_type: "fivetran",
        })
        setOnboardUrl(uri)
      } catch (error) {
        // The Fivetran path re-throws the RTK error, which carries the API's message
        // at error.data.message — including Fivetran's own setup errors (e.g. "Merchant
        // ID is required for Seller accounts."). Swallowing it left users with a generic
        // toast and nothing to act on (MD-5391).
        trackFailure({
          service_name: service,
          error_message: "Failed to connect to the service",
        })
        toast.error(
          getServerErrorMessage(
            error,
            "Failed to connect to the service. Try again later",
          ),
          {
            toastId: `fail-connect-to-service-${service}`,
          },
        )
      } finally {
        void callback?.()
      }
    },
    [
      connectedServices,
      getCompanyConnectorByService,
      getConnectorById,
      integrationsReady,
      connect,
      reAuth,
      refetchConnectors,
      trackOAuthRedirect,
      trackConnectionSuccess,
      trackConnectionFailed,
      trackReconnectFailed,
    ],
  )

  const addNewConnection = useCallback(
    async (service: string) => {
      if (isAddingConnection.current) return
      isAddingConnection.current = true
      try {
        const connector = getConnectorById(service)
        if (!connector || !isAirbyteConnector(connector)) {
          toast.error(
            "Multiple connections are only supported for Airbyte connectors",
            {
              toastId: `multi-conn-not-supported-${service}`,
            },
          )
          return
        }

        // Check pre-config requirement client-side to avoid triggering a full OAuth call
        // The requiresOAuthPreConfig flag is derived from the connector's additional_config schema
        if (connector.requiresOAuthPreConfig) {
          // Use existing connector ID for the AdditionalConfigForm to fetch property schemas
          const existingConnector = getCompanyConnectorByService(service)
          if (existingConnector) {
            setDialog(() => (
              <AdditionalConfigForm
                companyConnectorId={existingConnector.id}
                connector={connector}
                isOpen
                configPhase="oauth"
                onSubmit={async (preConfigValues: Record<string, unknown>) => {
                  try {
                    const { uri } = await connect(service, preConfigValues, {
                      forceNew: true,
                    })
                    if (uri) {
                      window.location.href = uri
                    }
                  } catch {
                    toast.error("Failed to initiate OAuth with pre-config", {
                      toastId: `oauth-preconfig-error-${service}`,
                    })
                  }
                }}
                onClose={closeDialog}
              />
            ))
            return
          }

          toast.error(
            "Could not load connector configuration. Try refreshing the page.",
            {
              toastId: `pre-config-missing-connector-${service}`,
            },
          )
          return
        }

        // No pre-config needed — create the new connection directly
        const { uri } = await connect(service, undefined, { forceNew: true })
        if (uri) {
          window.location.href = uri
          return
        }

        await refetchConnectors()
      } catch {
        trackConnectionFailed({
          service_name: service,
          connector_type: "airbyte",
          error_message: "Failed to add new connection",
        })
        toast.error("Failed to add new connection. Try again later", {
          toastId: `fail-add-connection-${service}`,
        })
      } finally {
        isAddingConnection.current = false
      }
    },
    [
      getConnectorById,
      getCompanyConnectorByService,
      connect,
      refetchConnectors,
      setDialog,
      closeDialog,
      trackConnectionFailed,
    ],
  )

  const openConnectionStatusDialog = (service: string) => {
    const companyConnector = getCompanyConnectorByService(service)
    if (!companyConnector) {
      toast.error(`Oops... Seems like this service is not ready yet.`)
      return
    }

    const connector = getConnectorById(service)
    const allowMultipleConnections =
      connector?.allowMultipleConnections === true
    const props = {
      label: connector?.name ?? "",
      imgSrc: connector?.icon ?? "",
    }

    const reconnect = async (targetCompanyConnectorId?: string) => {
      const targetConnector = targetCompanyConnectorId
        ? connectedServices?.find(s => s.id === targetCompanyConnectorId)
        : getCompanyConnectorByService(service)
      if (targetConnector?.metadata?.createdByMigration) {
        toast.error(
          "This connector was created by the system migration and cannot be re-authenticated. Please contact your administrator if you need to modify this connection.",
          {
            toastId: "migration-connector-reconnect-blocked",
          },
        )
        return
      }
      await connectToService(service, undefined, targetCompanyConnectorId)
    }

    const handleAddConnection = () => {
      trackAddConnectionClicked({
        service_name: service,
        connector_name: connector?.name,
        company_connector_id: companyConnector.id,
        allow_multiple_connections: allowMultipleConnections,
      })
      closeDialog()
      void addNewConnection(service)
    }

    setDialog(() => (
      <ConnectionStatusDialog
        isOpen
        onClose={closeDialog}
        onReconnect={reconnect}
        confirmedAt={companyConnector.confirmedAt}
        companyConnectorId={companyConnector.id}
        allowMultipleConnections={allowMultipleConnections}
        onAddConnection={
          allowMultipleConnections ? handleAddConnection : undefined
        }
        {...props}
      />
    ))
  }

  useEffect(() => {
    if (onboardUrl) {
      window.location.href = onboardUrl
    }
  }, [onboardUrl])

  // Sort connectors: Airbyte connectors first, then others
  const sortedConnectors = [...connectors].sort((a, b) => {
    // Airbyte connectors come first
    if (a.isAirbyte && !b.isAirbyte) return -1
    if (!a.isAirbyte && b.isAirbyte) return 1
    return 0
  })

  // Right after Facebook Marketing, or last when a search hides it.
  const metaAdvancedShown = showMetaAdvanced && isSuperAdmin
  const metaAdvancedAfterAnchor =
    metaAdvancedShown &&
    sortedConnectors.some(({ id }) => id === META_ADVANCED_ANCHOR_SERVICE)

  return (
    <>
      <div className="bg-system-blue-800 border border-system-blue-700 rounded-system-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-white text-left font-medium">
            {activeCategory}
          </h2>
          <div className="flex items-center gap-2">
            {onRefetch && (
              <button
                onClick={onRefetch}
                disabled={isRefetching}
                className="h-12 flex items-center gap-1.5 px-4 bg-basic-dark-blue hover:bg-opacity-80 text-white rounded-2xl border border-basic-blue transition-colors duration-200 text-sm font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                title="Refresh connectors"
              >
                <svg
                  className={`w-4 h-4 ${isRefetching ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </button>
            )}
            <SearchBar onSearch={onSearch} />
          </div>
        </div>

        <ul className="grid grid-cols-1 gap-x-4 gap-y-4">
          {sortedConnectors.map(connector => (
            <Fragment key={connector.id}>
              <SourceItem
                {...connector}
                serviceName={connector.id}
                image={{ src: connector.icon, alt: `${connector.name} Logo` }}
                onConnect={connectToService}
                onStatusCheck={openConnectionStatusDialog}
                integrationsReady={integrationsReady}
              />
              {metaAdvancedAfterAnchor &&
                connector.id === META_ADVANCED_ANCHOR_SERVICE && (
                  <MetaAdvancedItem />
                )}
            </Fragment>
          ))}
          {metaAdvancedShown && !metaAdvancedAfterAnchor && (
            <MetaAdvancedItem />
          )}
        </ul>
      </div>
      {Dialog}
    </>
  )
}
